import { useState, useCallback, useEffect, useRef } from 'react';

type HoverRef = (node: HTMLElement | null) => void;

/**
 * Custom hook to manage hover state for an element.
 *
 * @returns {[HoverRef, boolean]} A tuple containing a ref callback to attach to a DOM element and a boolean indicating hover state.
 *
 * @example
 * const [hoverRef, isHovered] = useHover();
 * return <div ref={hoverRef}>{isHovered ? 'Hovered' : 'Not Hovered'}</div>;
 */

export const useHover = (): [HoverRef, boolean] => {
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const hoverRef = useCallback<HoverRef>(node => {
    nodeRef.current = node;
  }, []);

  useEffect(() => {
    const node = nodeRef.current;

    if (node !== null) {
      node.addEventListener('mouseenter', handleMouseEnter);
      node.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        node.removeEventListener('mouseenter', handleMouseEnter);
        node.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [handleMouseEnter, handleMouseLeave]);

  return [hoverRef, isHovered];
};
